import {
  addMultiSortRule,
  moveMultiSortRule,
  type MultiSortRule,
  removeMultiSortRule,
  updateMultiSortRule,
  sanitizeMultiSortModel,
  areMultiSortModelsEqual,
  type MultiSortDirection,
  buildMultiSortColumnOptions,
} from './model';
import {
  useGridSelector,
  useGridRootProps,
  useGridApiContext,
  gridSortModelSelector,
  gridColumnDefinitionsSelector,
  gridVisibleColumnDefinitionsSelector,
} from '@mui/x-data-grid-premium';
import { Badge, Button } from '@mui/material';
import MultiSortPanel from './multi-sort-panel';
import { FormattedMessage, useIntl } from 'react-intl';
import type { GridColDef } from '@mui/x-data-grid-premium';
import SortRoundedIcon from '@mui/icons-material/SortRounded';
import type { DraftMultiSortRule, MultiSortFocusRequest } from './types';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';

export type DataGridMultiSortProps = {
  disabled?: boolean;
  getColumnLabel?: (column: GridColDef) => string;
};

const DataGridMultiSort = ({ disabled = false, getColumnLabel }: DataGridMultiSortProps) => {
  const intl = useIntl();
  const apiRef = useGridApiContext();
  const rootProps = useGridRootProps();
  const appliedGridModel = useGridSelector(apiRef, gridSortModelSelector);
  const gridColumns = useGridSelector(apiRef, gridColumnDefinitionsSelector);
  const visibleGridColumns = useGridSelector(apiRef, gridVisibleColumnDefinitionsSelector);
  const panelId = useId();
  const panelTitleId = `${panelId}-title`;
  const panelDescriptionId = `${panelId}-description`;
  const ruleIdSequence = useRef(0);

  const visibleFields = useMemo(() => new Set(visibleGridColumns.map(({ field }) => field)), [visibleGridColumns]);
  const activeFields = useMemo(
    () => new Set(appliedGridModel.filter(({ sort }) => sort === 'asc' || sort === 'desc').map(({ field }) => field)),
    [appliedGridModel]
  );
  const candidateColumns = useMemo(
    () =>
      gridColumns
        .filter(({ field }) => visibleFields.has(field) || activeFields.has(field))
        .map((column) => ({
          ...column,
          headerName: getColumnLabel?.(column) ?? column.headerName,
          hidden: !visibleFields.has(column.field),
        })),
    [activeFields, getColumnLabel, gridColumns, visibleFields]
  );
  const columnOptions = useMemo(
    () => buildMultiSortColumnOptions(candidateColumns, appliedGridModel, rootProps.sortingOrder),
    [appliedGridModel, candidateColumns, rootProps.sortingOrder]
  );
  const appliedModel = useMemo(
    () => sanitizeMultiSortModel(appliedGridModel, columnOptions),
    [appliedGridModel, columnOptions]
  );

  const [open, setOpen] = useState(false);
  const [baseModel, setBaseModel] = useState<MultiSortRule[]>([]);
  const [draftModel, setDraftModel] = useState<DraftMultiSortRule[]>([]);
  const [focusRequest, setFocusRequest] = useState<MultiSortFocusRequest | null>(null);
  const [announcement, setAnnouncement] = useState('');

  const createDraftModel = useCallback(
    (model: readonly MultiSortRule[]): DraftMultiSortRule[] =>
      model.map((rule) => ({
        ...rule,
        id: `${panelId}-rule-${++ruleIdSequence.current}`,
      })),
    [panelId]
  );

  const plainDraftModel = useMemo(() => draftModel.map(({ field, sort }) => ({ field, sort })), [draftModel]);
  const hasChanges = !areMultiSortModelsEqual(baseModel, plainDraftModel);
  const hasExternalChange = open && !areMultiSortModelsEqual(baseModel, appliedModel);
  const featureUnavailable = rootProps.disableColumnSorting || rootProps.disableMultipleColumnsSorting;

  useEffect(() => {
    if (!open) return;

    setDraftModel((currentModel) => {
      const sanitizedModel = sanitizeMultiSortModel(currentModel, columnOptions);

      if (areMultiSortModelsEqual(currentModel, sanitizedModel)) return currentModel;

      const currentRuleByField = new Map(currentModel.map((rule) => [rule.field, rule]));
      return sanitizedModel.map((rule) => currentRuleByField.get(rule.field) ?? createDraftModel([rule])[0]);
    });
  }, [columnOptions, createDraftModel, open]);

  if (featureUnavailable || columnOptions.length === 0) return null;

  const requestFocus = (ruleId: string | null) => {
    setFocusRequest((currentRequest) => ({
      ruleId,
      sequence: (currentRequest?.sequence ?? 0) + 1,
    }));
  };

  const resetDraftFromAppliedModel = () => {
    setBaseModel(appliedModel);
    setDraftModel(createDraftModel(appliedModel));
    setFocusRequest(null);
    setAnnouncement('');
  };

  const openPanel = () => {
    resetDraftFromAppliedModel();
    setOpen(true);
  };

  const closePanel = () => setOpen(false);

  const applyModel = () => {
    if (hasExternalChange) return;

    apiRef.current.setSortModel(sanitizeMultiSortModel(draftModel, columnOptions));
    setOpen(false);
  };

  const addRule = () => {
    const nextModel = addMultiSortRule(plainDraftModel, columnOptions);
    if (nextModel.length === plainDraftModel.length) return;

    const addedRule = nextModel[nextModel.length - 1];
    const id = `${panelId}-rule-${++ruleIdSequence.current}`;
    setDraftModel((currentModel) => [...currentModel, { ...addedRule, id }]);
    requestFocus(id);
    setAnnouncement('');
  };

  const updateField = (index: number, field: string) => {
    setDraftModel((currentModel) => updateMultiSortRule(currentModel, index, { field }, columnOptions));
  };

  const updateDirection = (index: number, sort: MultiSortDirection) => {
    setDraftModel((currentModel) => updateMultiSortRule(currentModel, index, { sort }, columnOptions));
  };

  const moveRule = (index: number, direction: -1 | 1) => {
    const movedRule = draftModel[index];
    const targetIndex = index + direction;
    if (!movedRule || targetIndex < 0 || targetIndex >= draftModel.length) return;

    setDraftModel((currentModel) => moveMultiSortRule(currentModel, index, direction));
    requestFocus(movedRule.id);

    const columnLabel = columnOptions.find(({ field }) => field === movedRule.field)?.label ?? movedRule.field;
    setAnnouncement(
      intl.formatMessage(
        {
          id: 'datagrid.multiSort.movedAnnouncement',
          defaultMessage: '{column}, {order}. önceliğe taşındı.',
        },
        { column: columnLabel, order: targetIndex + 1 }
      )
    );
  };

  const removeRule = (index: number) => {
    const removedRule = draftModel[index];
    if (!removedRule) return;

    const nextModel = removeMultiSortRule(draftModel, index);
    const nextFocusedRule = nextModel[index] ?? nextModel[index - 1];
    setDraftModel(nextModel);
    requestFocus(nextFocusedRule?.id ?? null);

    const columnLabel = columnOptions.find(({ field }) => field === removedRule.field)?.label ?? removedRule.field;
    setAnnouncement(
      intl.formatMessage(
        {
          id: 'datagrid.multiSort.removedAnnouncement',
          defaultMessage: '{column} sıralama ölçütü silindi.',
        },
        { column: columnLabel }
      )
    );
  };

  const clearRules = () => {
    setDraftModel([]);
    requestFocus(null);
    setAnnouncement(
      intl.formatMessage({
        id: 'datagrid.multiSort.clearedAnnouncement',
        defaultMessage: 'Tüm sıralama ölçütleri temizlendi.',
      })
    );
  };

  const activeCountLabel = intl.formatMessage(
    {
      id: 'datagrid.multiSort.activeCount',
      defaultMessage: '{count, plural, =0 {Sıralama ölçütü yok} other {# sıralama ölçütü}}',
    },
    { count: appliedModel.length }
  );

  return (
    <>
      <Badge
        color="primary"
        badgeContent={appliedModel.length}
        invisible={appliedModel.length === 0}
        max={99}
        overlap="rectangular"
        slotProps={{ badge: { 'aria-hidden': true } }}
        sx={{
          '& .MuiBadge-badge': {
            right: 6,
            top: 4,
            minWidth: 18,
            height: 18,
            px: 0.5,
            fontSize: '0.6875rem',
            fontWeight: 700,
            border: '2px solid',
            borderColor: 'background.paper',
          },
        }}>
        <Button
          color="primary"
          size="small"
          startIcon={<SortRoundedIcon />}
          disabled={disabled}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-controls={open ? panelId : undefined}
          aria-label={intl.formatMessage(
            {
              id: 'datagrid.multiSort.buttonAria',
              defaultMessage: 'Sıralamayı yönet. {activeCount}',
            },
            { activeCount: activeCountLabel }
          )}
          onClick={openPanel}>
          <FormattedMessage id="datagrid.multiSort.button" defaultMessage="Sıralama" />
        </Button>
      </Badge>

      <MultiSortPanel
        open={open}
        panelId={panelId}
        titleId={panelTitleId}
        descriptionId={panelDescriptionId}
        draftModel={draftModel}
        columnOptions={columnOptions}
        focusRequest={focusRequest}
        announcement={announcement}
        hasChanges={hasChanges}
        hasExternalChange={hasExternalChange}
        onClose={closePanel}
        onApply={applyModel}
        onReload={resetDraftFromAppliedModel}
        onAdd={addRule}
        onClear={clearRules}
        onFieldChange={updateField}
        onDirectionChange={updateDirection}
        onMove={moveRule}
        onRemove={removeRule}
      />
    </>
  );
};

export default DataGridMultiSort;
